import { useRef, type ReactNode } from "react";
import { cn } from "@/shared/utils/cn";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../shadcn/Dialog";

interface OverlayDialogProps {
  open?: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  contentClassName?: string;
}

export function OverlayDialog({
  open = true,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  contentClassName,
}: OverlayDialogProps) {
  const previousFocus = useRef<HTMLElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent
        overlayClassName="backdrop-blur-sm"
        className={cn("flex max-w-2xl flex-col overflow-hidden p-0", className)}
        onOpenAutoFocus={() => {
          previousFocus.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        }}
        onCloseAutoFocus={(event) => {
          // Route-driven dialogs have no DialogTrigger to restore focus to.
          event.preventDefault();
          const target = previousFocus.current;
          if (target?.isConnected && target !== document.body) target.focus();
          else document.getElementById("main-content")?.focus();
        }}
      >
        <DialogHeader className="shrink-0 border-b border-border p-5">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className={description ? undefined : "sr-only"}>
            {description ?? `${title} dialog`}
          </DialogDescription>
        </DialogHeader>
        <div className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain p-5", contentClassName)}>
          {children}
        </div>
        {footer && <DialogFooter className="shrink-0 border-t border-border p-5">{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
